import jszip from "jszip";
import * as xml2js from "xml2js";
import { get } from "lodash";
import { MediaMeta, MediaType, MediaUpsert, thor } from "../../../typings/thor";
import { Enums, Events } from "../../../thor.enums";
import { EnumHelpers } from "../../../utils/enum-helpers";
import { PREMIUM_DESCRIPTION_MIN_LENGTH } from "../../../thor.const";

enum Item {
    CategoryId = "id-categorie",
    Description = "description",
    OrderNbr = "numero-ordre",
    Highlight = "lot-phare",
    ExternalRef = "identifiant",
    InternalRef = "reference-etude",
    Height = "longueur",
    Width = "largeur",
    Depth = "profondeur",
    Images = "images",
    EstimateMin = "estimation-basse",
    EstimateMax = "estimation-haute",
    StartingPrice = "prix-depart",
    ReservePrice = "prix-reserve"
}

export class ImportPasserelleService {
    public static $inject: string[] = [
        "$log",
        "$q",
        "$rootScope",
        "$translate",
        "categoryService",
        "configService",
        "itemService",
        "mediaService",
        "mrSuricateService",
        "sitesService",
        "uploadService",
        "utilsService",
        "imageService"
    ];

    public keepItemsAfterImport: boolean = false;
    public addNewItemsOnly: boolean;

    protected $t;
    protected sale;
    protected summary;
    protected maxItemsNbr: number;

    private actualExternalRefs;
    private actualOrderNumberRefs: Array<boolean>;
    private extensionId: string;
    private extensionMajorVersion: string;
    private extensionDownload: string;
    private maxImagesPerItem: number;
    private globalDeferred;
    private summaryDefault = {
        items: {
            created: 0,
            updated: 0,
            notModified: 0,
            deleted: 0,
            total: 0
        },
        medias: {
            created: 0,
            updated: 0,
            notModified: 0
        },
        secretOrders: {
            update: 0,
            deleted: 0
        },
        errors: [],
        imageErrors: [],
        warnings: []
    };

    constructor(
        protected $log,
        protected $q: ng.IQService,
        protected $rootScope: ng.IRootScopeService,
        $translate: ng.translate.ITranslateService,
        private categoryService,
        configService,
        protected itemService,
        private mediaService,
        protected mrSuricate,
        private sitesService,
        private uploadService,
        protected utilsService,
        private imageService
    ) {
        this.$t = $translate.instant;
        this.sale = null;
        this.extensionId = this.sitesService.extension.id;
        this.extensionMajorVersion = this.sitesService.extension.majorVersion;
        this.extensionDownload = this.sitesService.extension.download;
        this.maxImagesPerItem = configService.items.maxImages;
    }

    public engineZipFile(sale, file) {
        this.sale = sale;
        this.globalDeferred = this.$q.defer();
        this.summary = angular.copy(this.summaryDefault);

        this.checkChromeExtension()
            .then(() => this.readFileAsArrayBuffer(file))
            .then(zipFile => this.extractXml(zipFile))
            .then(xml => this.engineXmlContent(xml))
            .then(() => {
                this.sendSummary();
                this.$rootScope.$broadcast(Events.PostMessageToV2);
                this.globalDeferred.resolve();
            })
            .catch(error => {
                this.sendSummary();
                this.globalDeferred.reject(error);
            });

        return this.globalDeferred.promise;
    }

    public engineXmlFile(sale, file) {
        this.sale = sale;
        this.globalDeferred = this.$q.defer();
        this.summary = angular.copy(this.summaryDefault);

        this.checkChromeExtension()
            .then(() => this.readFileAsText(file))
            .then(xml => this.engineXmlContent(xml))
            .then(() => {
                this.sendSummary();
                this.$rootScope.$broadcast(Events.PostMessageToV2);
                this.globalDeferred.resolve();
            })
            .catch(error => {
                this.sendSummary();
                this.globalDeferred.reject(error);
            });

        return this.globalDeferred.promise;
    }

    protected notify(action, data) {
        this.globalDeferred.notify({ action, data });
    }

    protected handleCreateErrors(error, itemData) {
        this.notify("incrementGauge", { gauge: "items", counter: "error" });
        this.notify("incrementGauge", { gauge: "medias", counter: "error", increment: itemData.images.length });
        this.$log.error("Error while creating/updating item : ", error);
        this.summary.errors.push({
            message: this.$t("erreur_lors_de_la_creation_du_lot", {
                REFERENCE: (<any>itemData.item).external_reference
            })
        });
        return this.$q.reject(error);
    }

    protected updateItem(item, itemData) {
        const patchedData: thor.ItemPatch = {};

        if (item.category.id !== itemData.item.category_id) {
            patchedData.category_id = itemData.item.category_id;
        }

        if (item.description !== itemData.item.description) {
            patchedData.description = itemData.item.description;
        }

        if (item.internal_reference !== itemData.item.internal_reference) {
            patchedData.internal_reference = itemData.item.internal_reference;
        }

        if (item.highlight !== itemData.item.highlight) {
            patchedData.highlight = itemData.item.highlight;
        }

        if (item.type !== itemData.item.type) {
            patchedData.type = itemData.item.type;
        }

        if (!angular.equals(item.meta, itemData.item.meta)) {
            patchedData.meta = itemData.item.meta;
        }

        if (!angular.equals(item.pricing, itemData.item.pricing)) {
            patchedData.pricing = itemData.item.pricing;
        }

        if (!angular.equals(item.features, itemData.item.features)) {
            patchedData.features = itemData.item.features;
        }

        if (item.states.suppressed === true || item.states.hidden === true) {
            patchedData.states = {
                suppressed: false,
                hidden: false
            };
        }

        if (angular.equals(patchedData, {})) {
            this.$log.info(`No changes detected for item : ${itemData.item.external_reference} `);
            this.summary.items.notModified++;
            return this.$q.resolve(item);
        }

        if (item.secretOrderCount > 0) {
            const orderNumberNormalized = this.utilsService.getOrderNumberNormalized(itemData.item);
            this.summary.secretOrders.updated += item.secretOrderCount;
            this.summary.warnings.push({
                message: `
                    ${this.$t("ce_lot_avec_oas_a_ete_modifie", {
                        OAS: this.$t("ordres_d_achat_secrets_abbr").toLowerCase()
                    })} \
                    ${itemData.item.external_reference}\
                    ${orderNumberNormalized ? this.$t("lot_n", { NUMBER: orderNumberNormalized }) : ""}
                `
            });
        }

        return this.itemService.update(item.id, patchedData).then(updatedItem => {
            this.summary.items.updated++;
            return this.$q.resolve(updatedItem);
        });
    }

    protected createItem(itemData) {
        return this.sale.createItem(itemData.item).then(item => {
            this.summary.items.created++;
            return this.$q.resolve(item);
        });
    }

    /*
     * skip published item updates when sale started (Online)
     * @param object item
     * @return boolean
     */
    protected skipItemPublished(item): boolean {
        return this.addNewItemsOnly && item.status !== Enums.ItemStatus.Created;
    }

    private engineXmlContent(xmlContent) {
        this.actualExternalRefs = [];
        this.actualOrderNumberRefs = [];

        return this.xmlToJson(xmlContent)
            .then(json => this.prepareItems(json))
            .then(formattedItemsData => this.engineItems(formattedItemsData))
            .then(() => {
                if (!this.keepItemsAfterImport) {
                    return this.deleteExtraItems();
                }
            });
    }

    private sendSummary() {
        this.notify("setSummary", this.summary);
    }

    private engineItems(formattedItemsData) {
        if (!this.maxItemsNbr) {
            return this.$q.series(formattedItemsData, this.engineItem.bind(this));
        }
        // set items total with already existing items
        this.summary.items.total = this.sale.getItemsPublishCount();

        return this.$q
            .series(formattedItemsData, this.checkMaxItemsReached.bind(this))
            .then(() => this.$q.series(formattedItemsData, this.engineItem.bind(this)))
            .catch(({ errorMessage }) => {
                this.summary.errors.push({ message: errorMessage });
                this.$q.reject();
            });
    }

    private checkMaxItemsReached(itemData) {
        const externalRef = itemData.item?.external_reference;
        const matchedExternalRef = this.sale.items.find(i => i.external_reference === externalRef);
        const orderNbr = itemData.item?.meta?.order_number;
        const matchedOrderNbr = this.utilsService.findItemByOrderNumber(orderNbr, this.sale.items);

        if (externalRef && !matchedExternalRef && (!orderNbr || !matchedOrderNbr)) {
            this.summary.items.total++;

            if (this.summary.items.total > this.maxItemsNbr) {
                return this.$q.reject({ errorMessage: this.$t("max_items_reached_error", [this.maxItemsNbr]) });
            }
        }
        return this.$q.resolve();
    }

    private engineItem(itemData) {
        return this.createOrUpdateItem(itemData)
            .then(item => this.getItemsMedias(item, itemData))
            .then(item => this.engineImages(item, itemData.images))
            .then(item => this.deleteExtraItemsMedias(item))
            .catch(error => {
                this.$log.error("Error while creating or updating item", error);
                // only a warning, keep process
                return this.$q.resolve();
            });
    }

    private deleteExtraItemsMedias(item) {
        if (this.skipItemPublished(item)) {
            this.$log.info("Skip deleteExtraItemsMedias", item);
            return this.$q.resolve();
        }

        return this.$q.series(item.medias, this.deleteItemMedia.bind(this));
    }

    private getItemsMedias(item, itemData) {
        return this.itemService
            .getMedias(item.id)
            .then(medias => {
                item.medias = medias;
                return this.$q.resolve(item);
            })
            .catch(error => {
                this.$log.error(this.$t("erreur_lors_de_la_recuperation_des_media_du_lot"), error);
                this.notify("incrementGauge", { gauge: "medias", counter: "error", increment: itemData.images.length });
                this.summary.warnings.push({
                    message: this.$t("erreur_lors_du_traitement_des_images_du_lot", {
                        REFERENCE: itemData.item.external_reference
                    })
                });
                return this.$q.reject(error);
            });
    }

    private deleteItemMedia(media) {
        return this.mediaService
            .deleteMedia(media.id)
            .then(res => this.$log.info(`Delete media ${media.id} `, res))
            .catch(error => this.$log.error(`Error on Delete media ${media.id} `, error));
    }

    private engineImages(item, imagesData) {
        return this.$q.series(imagesData, this.engineImage.bind(this, item)).then(() => {
            return this.$q.resolve(item);
        });
    }

    private engineImage(item, imageData) {
        return this.readImage(imageData.filepath)
            .then(localMediaData => this.createOrUpdateItemMedia(item, imageData, localMediaData))
            .then(() => {
                this.notify("incrementGauge", { gauge: "medias", counter: "valid" });
                return this.$q.resolve();
            })
            .catch(error => {
                this.$log.warn("warning while trying to read an image during import", error);
                this.notify("incrementGauge", { gauge: "medias", counter: "error" });
                // only a warning, keep process
                return this.$q.resolve();
            });
    }

    private deleteExtraItems() {
        const itemsToDelete = this.sale.items.filter(item =>
            item.external_reference &&
            this.actualExternalRefs[item.external_reference] !== true &&
            item.states.suppressed === false
        );

        // update summary warnings for deleted items with OAS
        itemsToDelete.forEach(item => {
            if (item.secretOrderCount > 0) {
                this.summary.secretOrders.deleted += item.secretOrderCount;
                const orderNumber = item.meta.order_number;
                const orderNumberStr = orderNumber.primary
                    ? orderNumber.secondary
                        ? `${this.$t("number_abr")}${orderNumber.primary}-${orderNumber.secondary}`
                        : `${this.$t("number_abr")}${orderNumber.primary}`
                    : "";
                this.summary.warnings.push({
                    message: this.$t("le_lot_x_avec_oas_sera_supprime_a_la_prochaine_publication", {
                        NUMBER: orderNumberStr,
                        OAS: this.$t("ordres_d_achat_secrets_abbr").toLowerCase(),
                        REFERENCE: item.external_reference
                    })
                });
            }
        });

        this.notify("setGauge", { gauge: "extraItems", counter: "total", value: itemsToDelete.length });

        return this.$q
            .series(itemsToDelete, item => this.deleteItem(item.id))
            .catch(error => this.$log.error("Error on delete extra items in series", error));
    }

    private deleteItem(itemId) {
        const data = {
            states: {
                suppressed: true
            }
        };

        return this.itemService
            .update(itemId, data)
            .then(res => {
                this.$log.info("Item states updated with success", res);
                this.summary.items.deleted++;
                this.notify("incrementGauge", { gauge: "extraItems", counter: "valid" });
            })
            .catch(error => {
                this.$log.error(`Error while updating the item ${itemId}`, error);
                this.notify("incrementGauge", { gauge: "extraItems", counter: "error" });
                this.summary.errors.push({
                    message: this.$t("erreur_lors_de_la_creation_mise_a_jour_du_lot", { ITEM_ID: itemId })
                });
            });
    }

    private checkChromeExtension() {
        // Check extension
        // @todo: move it to a dedicated service
        const deferred = this.$q.defer();

        const extensionError = response => {
            this.$log.error("Error with extension : checkVersion ", response.error);
            this.summary.errors.push({
                message: `
                    ${this.$t("probleme_de_communication_avec_l_extension_permettant_l_import_passerelle")}<br/>
                    ${this.$t("verifiez_son_installation_ici", {
                        LINK: `<a href="${this.extensionDownload}" target="_blank" translate>ici</a>`
                    })}
                `
            });
            deferred.reject("Error with extension : checkVersion");
            return deferred.promise;
        };

        if (this.mrSuricate.hasAccess()) {
            deferred.resolve({ currentVersion: this.mrSuricate.user });
            return deferred.promise;
        }

        if (!window.chrome || !window.chrome.runtime) {
            return extensionError({ error: "chrome.runtime is not defined" });
        }

        chrome.runtime.sendMessage(this.extensionId, { action: "check" }, response => {
            const lastError = chrome.runtime.lastError;
            if (lastError) {
                // Could not establish connection. Receiving end does not exist.
                this.$log.error("Error communicate with extension", lastError.message);
                this.summary.errors.push({
                    message: `
                            ${this.$t("l_extension_permettant_l_import_passerelle_n_est_pas_installee")}<br/>
                            ${this.$t("vous_pouvez_la_telecharger", {
                                LINK: `<a href="${this.extensionDownload}" target="_blank" translate>ici</a>`
                            })}
                        `
                });
                return deferred.reject("Error communicate with extension");
            }

            if (!response || response.error) {
                return extensionError(response);
            }

            if (!response.version.startsWith(`${this.extensionMajorVersion}.`)) {
                this.summary.errors.push({
                    message: `
                            ${this.$t("l_extension_permettant_l_import_passerelle_n_est_pas_a_jour")}<br/>
                            ${this.$t("vous_pouvez_la_mettre_a_jour_ici", {
                                LINK: `<a href="${this.extensionDownload}" target="_blank" translate>ici</a>`
                            })}
                        `
                });
                return deferred.reject(
                    `Error with extension ${response.version} does not \
match thor config ${this.extensionMajorVersion}.*`
                );
            }

            if (!response.isAllowedFileSchemeAccess) {
                this.summary.errors.push({
                    message: this.$t("veuiller_autoriser_l_acces_aux_fichiers_a_l_extension_atlas")
                });

                return deferred.reject("Extension isn't allowed to read local files");
            }

            return deferred.resolve();
        });

        return deferred.promise;
    }

    private readFileAsArrayBuffer(file) {
        const deferred = this.$q.defer();
        const reader = new FileReader();

        reader.onload = event => deferred.resolve((<any>event).target.result);
        reader.onerror = error => deferred.reject(error);
        reader.readAsArrayBuffer(file);

        return deferred.promise;
    }

    private readFileAsText(file) {
        const deferred = this.$q.defer();
        const reader = new FileReader();

        reader.onload = event => deferred.resolve((<any>event).target.result);
        reader.onerror = error => deferred.reject(error);
        reader.readAsText(file);

        return deferred.promise;
    }

    private extractXml(zipFile) {
        const zip = new jszip();

        return (<any>zip)
            .loadAsync(zipFile)
            .then(loadedZip => loadedZip.file("export.xml").async("string"))
            .catch(error => {
                this.summary.errors.push({
                    message: this.$t("aucun_fichier_export_xml_present_dans_le_zip")
                });
                return this.$q.reject(error);
            });
    }

    private xmlToJson(xmlContent) {
        const parser = new xml2js.Parser();
        const deferred = this.$q.defer();

        parser.parseString(xmlContent, (error, json) => {
            if (error) {
                this.summary.errors.push({
                    message: `${this.$t("le_fichier_xml_est_incorrect")} ${error}`
                });
                return deferred.reject(error);
            } else {
                return deferred.resolve(json);
            }
        });

        return deferred.promise;
    }

    private prepareItems(data) {
        // pre-fill this.actualOrderNumberRefs with manually created items
        this.sale.items.forEach(item => {
            if (!item.external_reference) {
                const orderNumberNormalized = this.utilsService.getOrderNumberNormalized(item);
                if (orderNumberNormalized) {
                    this.actualOrderNumberRefs[orderNumberNormalized] = true;
                }
            }
        });

        // Check main element
        if (!data["import-lots"] || !data["import-lots"].lots || !data["import-lots"].lots[0]) {
            this.summary.errors.push({
                message: this.$t("le_fichier_xml_est_incomplet")
            });
            return this.$q.reject("incomplete XML file");
        }

        const lots = data["import-lots"].lots[0].lot;

        // Check lots
        if (!lots || lots.length <= 0) {
            this.summary.errors.push({
                message: this.$t("aucun_lot_dans_le_xml")
            });
            return this.$q.reject("no items in XML file");
        }

        this.notify("setGauge", { gauge: "items", counter: "total", value: lots.length });

        return this.$q.series(lots, this.prepareItem.bind(this));
    }

    private prepareItem(lot) {
        // external_reference
        if (!lot[Item.ExternalRef] || !lot[Item.ExternalRef][0]) {
            this.summary.errors.push({
                message: this.$t("invalid_xml.presence_d_un_lot_sans_identifiant")
            });
            return this.$q.reject(`missing external reference ${JSON.stringify(lot)}`);
        }

        const externalReference = lot[Item.ExternalRef][0].toString();

        if (this.actualExternalRefs[externalReference] === true) {
            this.summary.errors.push({
                message: this.$t("invalid_xml.plusieurs_lots_avec_des_identifiants_identiques", [
                    `${externalReference}`
                ])
            });
            return this.$q.reject(`duplicate external reference : ${externalReference}`);
        }

        this.actualExternalRefs[externalReference] = true;

        // @todo set var with utilsService.orderNumberNormalized()
        let orderNumberNormalized = (lot[Item.OrderNbr][0].numero + "-" + lot[Item.OrderNbr][0].bis)
            .toString()
            .replace(/\s/g, "");

        if (this.actualOrderNumberRefs[orderNumberNormalized] === true) {
            this.summary.errors.push({
                message: this.$t("invalid_xml.un_lot_a_deja_ce_numero_d_ordre", { NUMBER: orderNumberNormalized })
            });
            return this.$q.reject(`duplicate order number : ${orderNumberNormalized}`);
        }

        if (orderNumberNormalized !== "-" && orderNumberNormalized !== "0-") {
            this.actualOrderNumberRefs[orderNumberNormalized] = true;
        }

        // prepare item data
        let itemMedias = [];
        const itemData: thor.ItemPost = {
            description: null,
            meta: {},
            internal_reference: null,
            features: null,
            type: null,
            pricing: null,
            highlight: false,
            category_id: this.sale.resource.category_id,
            external_reference: externalReference
        };
        const features: thor.ItemFeatures = {};
        const pricing: thor.ItemPricing = {};
        const estimates: thor.ItemPricingEstimates = {};
        const meta: thor.ItemMeta = {};
        const orderNumber: thor.ItemMetaOrderNumber = {};

        // internal_reference
        if (lot[Item.InternalRef] && lot[Item.InternalRef][0]) {
            itemData.internal_reference = lot[Item.InternalRef][0].toString();
        }

        // highlight
        if (lot[Item.Highlight] && lot[Item.Highlight][0] && lot[Item.Highlight][0].toString() === "1") {
            itemData.highlight = true;
        }

        // description
        if (lot[Item.Description] && lot[Item.Description][0]) {
            itemData.description = lot[Item.Description][0].toString();
        }

        // meta
        if (lot[Item.OrderNbr]?.[0]) {
            const orderNumberPrimary = parseInt(lot[Item.OrderNbr][0].numero?.[0].toString(), 10);
            const orderNumberSecondary = lot[Item.OrderNbr][0].bis?.[0].toString().replace(/\s/g, "");

            if (orderNumberPrimary) {
                orderNumber.primary = orderNumberPrimary;

                if (orderNumberSecondary) {
                    orderNumber.secondary = orderNumberSecondary;
                }
                meta.order_number = orderNumber;
            }
        }

        if (!angular.equals(meta, {})) {
            itemData.meta = meta;
        }

        orderNumberNormalized = this.utilsService.getOrderNumberNormalized(itemData);

        const data = this.pickAndFormatNbr(lot, <Item[]>EnumHelpers.getValues(Item));

        // features
        if (data[Item.Height]) {
            features.height = data[Item.Height];
        }

        if (data[Item.Width]) {
            features.width = data[Item.Width];
        }

        if (data[Item.Depth]) {
            features.depth = data[Item.Depth];
        }

        if (!angular.equals(features, {})) {
            itemData.features = features;
        }

        // pricing
        estimates.min = data[Item.EstimateMin] || null;
        estimates.max = data[Item.EstimateMax] || null;
        pricing.estimates = estimates.min || estimates.max ? estimates : null;

        if (data[Item.StartingPrice]) {
            pricing.starting_price = data[Item.StartingPrice];
        }

        if (data[Item.ReservePrice]) {
            pricing.reserve_price = data[Item.ReservePrice];
        }

        if (!angular.equals(pricing, {})) {
            itemData.pricing = pricing;
        }

        // categories
        if (data[Item.CategoryId]) {
            const categoryId = data[Item.CategoryId];
            const field = this.categoryService.getCategoryField(categoryId);

            if (field) {
                itemData.category_id = categoryId;
            } else {
                this.$log.error(`Trying to set wrong category ${categoryId}`);
            }
        }

        const findItem = this.sale.items.find(i => i.external_reference === itemData.external_reference);

        // check criteria validity (premium)
        if (findItem?.asked_for_premium) {
            if (!this.categoryService.isCategoryPremium({ category: { id: itemData.category_id }})) {
                this.summary.errors.push({
                    message: `${
                        orderNumberNormalized ? `${this.$t("lot_n", { NUMBER: orderNumberNormalized })} ` : ""
                    }${this.$t("invalid_xml.category_incompatible_premium_lot", [
                        this.categoryService.getCategoryName(itemData.category_id)
                    ])}<br><small>${itemData.external_reference}</small>`});
                return this.$q.reject(`Invalid category with Premium lot ${JSON.stringify(lot)}`);
            }

            if (itemData.description?.length < PREMIUM_DESCRIPTION_MIN_LENGTH) {
                this.summary.errors.push({ message: `${
                    orderNumberNormalized ? `${this.$t("lot_n", { NUMBER: orderNumberNormalized })} ` : ""
                }${this.$t("invalid_xml.error")} ${this.$t("refusal_reasons.description")}<br><small>${
                    itemData.external_reference
                }</small>`});
                return this.$q.reject(`Invalid description length with Premium lot ${JSON.stringify(lot)}`);
            }
        }

        // check estimates validity
        if (estimates.min && estimates.max && estimates.min > estimates.max) {
            this.summary.errors.push({
                message: this.$t(
                    "lot_n_mise_a_jour_impossible_l_estimation_basse_doit_etre_inferieure_a_l_estimation_haute",
                    [orderNumberNormalized]
                )
            });
            return this.$q.reject(`Uneven min/max estimates range values ${JSON.stringify(lot)}`);
        }

        // medias
        if (
            !lot[Item.Images] ||
            !lot[Item.Images][0] ||
            !lot[Item.Images][0].image ||
            lot[Item.Images][0].image.length <= 0
        ) {
            if (findItem?.asked_for_premium) {
                this.summary.errors.push({ message: `${
                    orderNumberNormalized ? `${this.$t("lot_n", { NUMBER: orderNumberNormalized })} ` : ""
                }${this.$t("invalid_xml.error")} ${this.$t("refusal_reasons.media")}<br><small>${
                    itemData.external_reference
                }</small>`});

                return this.$q.reject(`Unset media with Premium lot ${JSON.stringify(lot)}`);
            } else {
                this.summary.warnings.push({
                    message: this.$t("le_lot_n_a_pas_d_images", { REFERENCE: externalReference })
                });
            }
        } else {
            const images = lot[Item.Images][0].image;

            images.forEach(image => {
                if (!image.chemin || !image.chemin[0]) {
                    this.summary.warnings.push({
                        message: this.$t("le_lot_possede_une_image_avec_un_chemin_incorrect", {
                            REFERENCE: externalReference
                        })
                    });
                } else {
                    const filepath = image.chemin[0].toString();
                    let filename;
                    let rank = null;
                    let description = null;

                    // Get filename
                    // Detect windows path
                    if (filepath.indexOf("\\") !== -1) {
                        filename = filepath.split("\\").pop();
                    } else {
                        filename = filepath.split("/").pop();
                    }

                    if (image.legende && image.legende[0]) {
                        description = image.legende[0].toString();
                    }

                    rank = 0;
                    if (image.rang && image.rang[0]) {
                        rank = parseInt(image.rang[0].toString(), 10);
                    }

                    itemMedias.push({ filename, filepath, rank, description });
                }
            });

            itemMedias.sort((a, b) => a.rank - b.rank);

            // ensure rank order
            itemMedias.forEach((image, index) => (image.rank = index));

            if (itemMedias.length > this.maxImagesPerItem) {
                this.$log.warn(
                    `Item ${externalReference} has ${itemMedias.length} images\
and exceeds the limit(${this.maxImagesPerItem})`
                );
                this.summary.warnings.push({
                    message: this.$t("le_lot_a_x_images_et_excede_la_limite", {
                        REFERENCE: externalReference,
                        LENGTH: this.maxImagesPerItem
                    })
                });
                itemMedias = itemMedias.slice(0, this.maxImagesPerItem);
            }

            this.$log.info(`Item ${externalReference} has ${itemMedias.length} images`);
            this.notify("incrementGauge", { gauge: "medias", counter: "total", increment: itemMedias.length });
        }

        return this.$q.resolve({
            item: itemData,
            images: itemMedias
        });
    }

    private createOrUpdateItem(itemData) {
        let promise;

        if (!itemData.item.external_reference) {
            promise = this.$q.reject("Item with no external_reference");
        } else {
            const matchedItem = this.sale.items.find(i => i.external_reference === itemData.item.external_reference);

            if (matchedItem) {
                promise = this.updateItem(matchedItem, itemData);
            } else {
                const incomingOrderNumber = get(itemData, "item.meta.order_number");
                if (
                    incomingOrderNumber &&
                    this.utilsService.findItemByOrderNumber(incomingOrderNumber, this.sale.items)
                ) {
                    const orderNumberNormalized = this.utilsService.getOrderNumberNormalized(itemData.item);
                    promise = this.$q.reject({
                        errorMessage: this.$t("un_lot_a_deja_ce_numero_d_ordre", [orderNumberNormalized])
                    });
                } else {
                    promise = this.createItem(itemData);
                }
            }
        }

        return promise
            .then(item => {
                this.notify("incrementGauge", { gauge: "items", counter: "valid" });
                return this.$q.resolve(item);
            })
            .catch(error => this.handleCreateErrors(error, itemData));
    }

    private pickAndFormatNbr(obj: any, keys: Item[]) {
        const isTypeNumber = [
            Item.CategoryId,
            Item.Height,
            Item.Width,
            Item.Depth,
            Item.EstimateMin,
            Item.EstimateMax,
            Item.StartingPrice,
            Item.ReservePrice
        ];
        const res = {};

        for (const key of keys) {
            if (!isTypeNumber.includes(key) || !obj[key]) {
                continue;
            }
            const nbr: number = Number(obj[key][0]);

            if (!Number.isInteger(nbr) || !(Math.sign(nbr) > 0)) {
                continue;
            }

            // set positive number only
            res[key] = nbr;
        }
        return res;
    }

    private readImage(filepath) {
        const deferred = this.$q.defer();

        if (this.mrSuricate.hasAccess()) {
            deferred.resolve({ currentVersion: this.mrSuricate.user });
            return deferred.promise;
        }

        // Request file to extension
        chrome.runtime.sendMessage(
            this.extensionId,
            {
                action: "readImage",
                path: filepath
            },
            response => {
                if (!response || response.error) {
                    this.$log.error(`Error with extension : readImage ${filepath}`, response);
                    this.summary.imageErrors.push({
                        message: `Impossible de lire l'image : ${filepath}`
                    });
                    return deferred.reject(`Error with extension : readImage ${filepath}`);
                } else {
                    this.$log.info(`File found : ${filepath}`);
                    const res = this.utilsService.dataURItoBlob(response.data, false);
                    if (!res) {
                        this.summary.imageErrors.push({
                            message: `${this.$t("impossible_de_convertir_l_image")} : ${filepath}`
                        });
                        return deferred.reject(`Impossible to import image ${filepath}`);
                    }
                    this.imageService
                        .resizeImage({ blob: res.blob, dataURI: response.data }, true)
                        .then(data => {
                            const localMediaData = this.utilsService.dataURItoBlob(data);

                            if (!localMediaData || !localMediaData.blob) {
                                this.$log.error(`Error converting data from extension ${filepath}`, response);
                                this.summary.imageErrors.push({
                                    message: `${this.$t("impossible_de_convertir_l_image")} : ${filepath}`
                                });
                                return deferred.reject("Error converting data from extension");
                            }

                            return deferred.resolve(localMediaData);
                        })
                        .catch(deferred.reject);
                }
            }
        );

        return deferred.promise;
    }

    private uploadImage(imageData, blob) {
        const content = {
            type: MediaType.ItemPicture,
            meta: {
                name: imageData.filename
            }
        };

        if (imageData.rank) {
            (<any>Object).assign(content, { rank: imageData.rank });
        }

        if (imageData.description) {
            (<any>Object).assign(content.meta, { description: imageData.description });
        }

        return this.uploadService
            .uploadFile(blob, content)
            .then(media => this.$q.resolve(media))
            .catch(error => {
                this.$log.error("Error while upload images", error);
                this.summary.imageErrors.push({
                    message: `${this.$t("impossible_de_telecharger_l_image")} ${imageData.filepath}`
                });
                return this.$q.reject(error);
            });
    }

    private createOrUpdateItemMedia(item, imageData, localMediaData) {
        let promise;

        if (this.skipItemPublished(item)) {
            return this.skipItemMedia(item);
        }

        const matchedItemMedia = item.medias.find((media, i) => {
            if (media.md5sum === localMediaData.md5) {
                // Remove it from item.medias
                // The remaining item.medias will be suppressed later by "deleteExtraItemsMedias"
                item.medias.splice(i, 1);
                return true;
            } else {
                return false;
            }
        });

        if (matchedItemMedia) {
            promise = this.updateItemMedia(matchedItemMedia, imageData);
        } else {
            promise = this.createItemMedia(item, imageData, localMediaData.blob);
        }

        return promise
            .then(itemMedia => {
                this.$log.info("Item media created/updated : ", itemMedia);
                return this.$q.resolve();
            })
            .catch(error => {
                this.$log.error("Error while created item media", error);
                this.summary.imageErrors.push({
                    message: this.$t("impossible_de_lier_le_lot_et_l_image", [
                        item.external_reference,
                        imageData.filepath
                    ])
                });
                return this.$q.reject(error);
            });
    }

    private createItemMedia(item, imageData, blob) {
        return this.uploadImage(imageData, blob)
            .then(media => {
                const itemMediaPost: MediaUpsert = {
                    media_id: media.id,
                    type: MediaType.ItemPicture,
                    rank: imageData.rank
                };
                if (imageData.description) {
                    itemMediaPost.meta = <MediaMeta>{
                        description: imageData.description
                    };
                }
                return this.itemService.createMedia(item.id, itemMediaPost)
                    .then(createdItemMedia => {
                        this.summary.medias.created++;
                        return this.$q.resolve(createdItemMedia);
                    });
            });
    }

    private updateItemMedia(itemMedia, imageData) {
        if (itemMedia.rank === imageData.rank && itemMedia.meta.description === imageData.description) {
            this.$log.info("No changes detected for itemMedia");
            this.summary.medias.notModified++;
            return this.$q.resolve(itemMedia);
        }

        const itemMediaPatch: MediaUpsert = {
            rank: imageData.rank
        };

        itemMediaPatch.meta = imageData.meta || {};
        itemMediaPatch.meta.description = imageData.description;

        return this.mediaService.updateMedia(itemMedia.id, itemMediaPatch).then(updatedItemMedia => {
            this.summary.medias.updated++;
            return this.$q.resolve(updatedItemMedia);
        });
    }

    private skipItemMedia(item) {
        this.$log.info("Skip changes for itemMedia", item);
        this.summary.medias.notModified++;
        return this.$q.resolve();
    }
}
